import { useEffect, useMemo, useRef } from "react";
import { useT } from "../lib/i18n";
import { loadWebSession } from "../lib/authSession";
import { brandInitials } from "../lib/branding";
import { useBranding } from "../lib/brandingContext";
import { greetingName } from "../lib/greeting";
import { pageHref, type Page, type RoutePage } from "../routes";
import { NAV_ACCOUNT, NAV_GROUPS, type NavItem } from "../lib/navItems";
import { useShell } from "./ShellContext";
import { DsIcon } from "./DsIcon";
import { cx } from "../lib/cx";

interface Props {
  current: RoutePage;
  onNavigate: (page: Page) => void;
  onToggleCollapse?: () => void;
}

function initialsOf(name: string): string {
  const parts = name.split(/[\s._@-]+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export function Sidebar({ current, onNavigate }: Props) {
  const t = useT();
  const shell = useShell();
  const { branding } = useBranding();
  const orgBrandName = branding?.displayName ?? null;
  const firstItemRef = useRef<HTMLButtonElement | null>(null);
  const session = useMemo(() => loadWebSession(), []);
  const org = session?.organizationName || session?.organizationSlug || "";
  const person = greetingName(session ?? {});
  const initials = initialsOf(org || person);

  useEffect(() => {
    if (shell.narrow && shell.sidebarOpen) firstItemRef.current?.focus();
  }, [shell.narrow, shell.sidebarOpen]);

  const renderItem = (item: NavItem, first = false) => {
    const disabled = item.page == null;
    const active = item.page != null && current === item.page;
    const label = t(item.labelKey);
    const hint = disabled && item.hintKey ? t(item.hintKey) : undefined;
    return (
      <button
        key={item.id}
        ref={first ? firstItemRef : undefined}
        type="button"
        className={cx(
          "flex h-8 w-full cursor-pointer items-center gap-2 rounded-ds-pill border-0 bg-transparent px-2.5 text-left font-ds text-ds-body leading-5 text-ds-ink-2 [font-family:inherit]",
          "hover:bg-ds-hover hover:text-ds-ink",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ds-ink",
          active && "bg-ds-glass-strong font-medium text-ds-ink shadow-[0_8px_20px_-12px_rgba(60,60,90,0.35)]",
          disabled && "cursor-default text-ds-ink-3 hover:bg-transparent hover:text-ds-ink-3",
        )}
        aria-current={active ? "page" : undefined}
        aria-disabled={disabled ? true : undefined}
        title={hint}
        data-testid={`nav-${item.id}`}
        onClick={() => {
          if (item.page) onNavigate(item.page);
        }}
      >
        <DsIcon svg={item.icon} className="size-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {item.badgeKey ? (
          <span className="shrink-0 rounded-ds-pill border border-ds-hair px-2 py-[3px] text-ds-badge font-normal leading-4 text-ds-ink-3">
            {t(item.badgeKey)}
          </span>
        ) : null}
      </button>
    );
  };

  return (
    <nav
      className={cx(
        "flex h-full w-[240px] shrink-0 flex-col border-r border-ds-hair bg-ds-glass px-2 py-3 font-ds text-ds-ink backdrop-blur-ds backdrop-saturate-[1.1]",
        shell.narrow &&
          cx(
            "fixed inset-y-0 left-0 z-50 transition-transform duration-200 ease-out",
            shell.sidebarOpen
              ? "translate-x-0 shadow-xl"
              : "pointer-events-none -translate-x-full",
          ),
      )}
      aria-label={t("sidebar.ariaMain")}
      data-testid="sidebar"
    >
      <a
        className="mb-3.5 flex items-start px-2 pt-1.5 font-logo text-lg leading-none no-underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ds-ink"
        href={pageHref("home")}
        aria-label={orgBrandName ? t("sidebar.orgMarkAria", { name: orgBrandName }) : t("sidebar.markAria")}
        data-testid="nav-mark"
        onClick={(e) => {
          e.preventDefault();
          onNavigate("home");
        }}
      >
        {orgBrandName ? (
          // Workspace host (#85): the org's logo / accent tile and name replace the wordmark.
          <span className="flex min-w-0 items-center gap-2 font-sans" data-testid="nav-org-brand">
            {branding?.logoUrl ? (
              <img
                src={branding.logoUrl}
                alt=""
                className="block h-6 w-auto max-w-[96px] shrink-0 object-contain"
                referrerPolicy="no-referrer"
                data-testid="nav-org-logo"
              />
            ) : (
              <span
                className="inline-flex size-6 shrink-0 items-center justify-center rounded-md bg-[color:var(--org-accent,var(--color-primary))] text-[11px] font-semibold text-[color:var(--org-accent-ink,#ffffff)]"
                aria-hidden="true"
              >
                {brandInitials(orgBrandName)}
              </span>
            )}
            <span className="min-w-0 truncate text-base font-semibold leading-6 text-ds-ink">{orgBrandName}</span>
          </span>
        ) : (
          <>
            <span className="font-semibold text-ds-ink">Semanti</span>
            <span className="font-extrabold text-ds-teal">CAD</span>
          </>
        )}
      </a>

      <div className="flex flex-col">
        {NAV_GROUPS.map((group, gi) => (
          <div className="flex flex-col gap-0.5 pb-3" key={group.labelKey}>
            <p className="m-0 flex h-8 items-center pl-2 text-ds-label leading-4 text-ds-ink-3">
              {t(group.labelKey)}
            </p>
            {group.items.map((item, i) => renderItem(item, gi === 0 && i === 0))}
          </div>
        ))}
      </div>

      <div className="min-h-2 flex-1" />

      <div className="flex flex-col gap-2">
        {NAV_ACCOUNT.map((item) => renderItem(item))}
        <div className="flex items-center gap-2 border-t border-ds-hair px-2 pt-3">
          <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-ds-glass-strong text-ds-badge font-semibold text-ds-ink">
            {initials || "—"}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-ds-body font-semibold leading-5 text-ds-ink">
              {org || person || t("sidebar.workspace")}
            </span>
            {session?.email ? (
              <span className="block truncate text-ds-badge leading-4 text-ds-ink-3">{session.email}</span>
            ) : null}
          </span>
        </div>
      </div>
    </nav>
  );
}
